import { useState } from "react";
import { LoaderFunction, useLoaderData, useNavigate } from "react-router-dom";

import { get } from "api";
import { Models } from "api/types";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ArrowLeft from "atoms/icon/icons/arrow_left.svg";
import SupportBubbles from "atoms/icon/icons/support_bubbles.svg";
import { ReturnItemSelection } from "modules/retailProducts/ReturnItemSelection/ReturnItemSelection";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";

const load = async (orderId: string) => {
  const platform = await queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const platformLocation = await queryClient.fetchQuery(
    ["/platform-location"],
    () => get("/platform-location")
  );

  let retailProductOrder = null;
  if (orderId) {
    try {
      retailProductOrder = await queryClient.fetchQuery(
        ["/retailproducts/orders/{orderId}", { orderId }],
        () => get("/retailproducts/orders/{orderId}", { orderId })
      );
    } catch (error) {
      console.error("Failed to fetch retail product order:", error);
      retailProductOrder = null;
    }
  }

  const retailProductRefunds = await queryClient.fetchQuery(
    [
      "/retailproducts/{platformId}/refund-support-details/{orderId}",
      { platformId: platform.id, orderId },
    ],
    () =>
      get("/retailproducts/{platformId}/refund-support-details/{orderId}", {
        platformId: platform.id,
        orderId,
      })
  );

  const storefrontSettings = await queryClient
    .fetchQuery(["/retailproducts/storefront-settings"], () =>
      get("/retailproducts/storefront-settings")
    )
    .catch(() => ({ supportEmailAddresses: [], refundEligibilityDays: 30 }));

  return {
    platform,
    platformLocation,
    retailProductOrder,
    retailProductRefunds,
    storefrontSettings,
  };
};

export type LoaderData = Awaited<ReturnType<typeof load>>;
export const ReturnsLoader: LoaderFunction = async ({ params }) => {
  const orderId = params.id;

  if (!orderId) {
    return await load("");
  }

  return await load(orderId);
};

const OrderNotFound = ({ navigate }: { navigate: any }) => {
  return (
    <div className="flex min-h-screen flex-col justify-between bg-gradient-to-bl from-brand-color/40 via-brand-tint to-white">
      <TopNav
        startIconSVG={ArrowLeft}
        onStartIconClick={() => {
          navigate(-1);
        }}
      >
        Order Details
      </TopNav>
      <div className="flex flex-grow flex-col items-center justify-center px-6 pt-10">
        <Icon svg={SupportBubbles} color="brandColor" size="page" />
        <div className="mt-6 text-center font-serif text-2xl text-primary">
          <h3>Order not found</h3>
        </div>
      </div>
    </div>
  );
};

const MyProductsReturns = ({
  platformLocation,
  supportEmailAddresses,
  navigate,
}: {
  platformLocation: any;
  supportEmailAddresses: string[];
  navigate: any;
}) => {
  const handleCallPractice = () => {
    if (platformLocation?.phoneNumber) {
      window.open(`tel:${platformLocation.phoneNumber}`, "_self");
    }
  };

  const handleEmailPractice = () => {
    if (supportEmailAddresses.length > 0) {
      window.open(`mailto:${supportEmailAddresses.join(",")}`, "_self");
    }
  };

  return (
    <>
      <TopNav
        startIconSVG={ArrowLeft}
        onStartIconClick={() => {
          navigate(-1);
        }}
      >
        Order Details
      </TopNav>
      <div className="flex min-h-screen flex-col justify-between bg-gradient-to-bl from-brand-color/40 via-brand-tint to-white">
        <div className="flex flex-grow flex-col items-center justify-center px-6 pt-10">
          <Icon svg={SupportBubbles} color="brandColor" size="page" />
          <div className="mt-6 text-center font-serif text-2xl text-primary">
            <h3>Need help with your order?</h3>
            <h3>Contact us.</h3>
          </div>
        </div>

        <div className="flex flex-col gap-4 px-6 pb-24">
          {supportEmailAddresses.length > 0 && (
            <Button
              fullWidth
              style="primary"
              size="medium"
              onClick={handleEmailPractice}
            >
              Email us
            </Button>
          )}
          <Button
            fullWidth
            style="primary"
            size="medium"
            onClick={handleCallPractice}
          >
            Call us
          </Button>
        </div>
      </div>
    </>
  );
};

const SkinDropReturns = ({
  orderId,
  lineItemsForShipping,
  navigate,
  retailProductRefunds,
}: {
  orderId: string;
  lineItemsForShipping: Models<"DisplayRetailProductOrderLineItemForShipping">[];
  navigate: any;
  retailProductRefunds: Models<"DisplayRetailProductRefundSupportDetailsForOrder">;
}) => {
  const [selectedItem, setSelectedItem] =
    useState<Models<"DisplayRetailProductOrderLineItemForShipping">>();

  return (
    <>
      <TopNav
        startIconSVG={ArrowLeft}
        onStartIconClick={() => {
          navigate(-1);
        }}
      >
        Order Details
      </TopNav>
      <ReturnItemSelection
        lineItemsForShipping={lineItemsForShipping}
        orderId={orderId}
        selectedItem={selectedItem}
        onSelectionChange={setSelectedItem}
        retailProductRefunds={retailProductRefunds}
      />
    </>
  );
};

export default function ReturnsPage() {
  const {
    platformLocation,
    retailProductOrder,
    retailProductRefunds,
    storefrontSettings,
  } = useLoaderData() as LoaderData;
  const navigate = useNavigate();

  if (!retailProductOrder) {
    return <OrderNotFound navigate={navigate} />;
  }

  const mixedOrder =
    retailProductOrder.lineItemsForShipping.length > 0 &&
    retailProductOrder.lineItemsForPickup.length > 0;

  if (retailProductOrder.lineItemsForShipping.length > 0 || mixedOrder) {
    return (
      <SkinDropReturns
        orderId={retailProductOrder.orderID}
        lineItemsForShipping={retailProductOrder.lineItemsForShipping}
        navigate={navigate}
        retailProductRefunds={retailProductRefunds}
      />
    );
  }

  return (
    <MyProductsReturns
      platformLocation={platformLocation}
      supportEmailAddresses={storefrontSettings?.supportEmailAddresses || []}
      navigate={navigate}
    />
  );
}
